import {
  Grid,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import InputField from '../../elements/InputField';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';

const initialCustomerState = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  password: '',
  submit: null,
};

const BaseCustomerSchema = Yup.object().shape({
  firstName: Yup.string().max(255).required('First Name is required'),
  lastName: Yup.string().max(255),
  email: Yup.string()
    .email('Must be a valid email')
    .max(255)
    .required('Email is required'),
  organizationName: Yup.string()
    .max(255)
    .required('Organization Name is required'),
});

const InviteSchema = BaseCustomerSchema;

const CreateSchema = BaseCustomerSchema.shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
});

const EditSchema = BaseCustomerSchema;

const CustomerBaseFormFields = ({
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  isEdit = false,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <InputField
        label="First Name"
        id="firstName"
        type="text"
        value={values.firstName}
        name="firstName"
        fullWidth
        required={true}
        placeholder="Enter first name"
        error={Boolean(touched.firstName && errors.firstName)}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <InputField
        label="Last Name"
        id="lastName"
        type="text"
        value={values.lastName}
        name="lastName"
        fullWidth
        placeholder="Enter last name"
        error={Boolean(touched.lastName && errors.lastName)}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Grid>
    <Grid item xs={12}>
      <InputField
        label="Email Address"
        id="email"
        type="email"
        value={values.email}
        name="email"
        required={true}
        fullWidth
        placeholder="Enter email address"
        error={Boolean(touched.email && errors.email)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={isEdit}
      />
    </Grid>
    <Grid item xs={12}>
      <InputField
        label="Name"
        id="organizationName"
        type="text"
        value={values.organizationName}
        name="organizationName"
        fullWidth
        required={true}
        placeholder="Enter organization name"
        error={Boolean(touched.organizationName && errors.organizationName)}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Grid>
  </Grid>
);

export const InviteCustomerForm = ({ handleInviteSubmit, loading }) => {
  return (
    <Formik
      initialValues={initialCustomerState}
      validationSchema={InviteSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleInviteSubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Form noValidate onSubmit={handleSubmit} className="p-3">
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
          />
          {errors.submit && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Grid>
          )}
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting || loading}
            className="w-100 mt-3">
            {isSubmitting || loading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              'Send Invitation'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export const CreateCustomerForm = ({ handleCreateDirectlySubmit, loading }) => {
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  return (
    <Formik
      initialValues={initialCustomerState}
      validationSchema={CreateSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleCreateDirectlySubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Form noValidate onSubmit={handleSubmit} className="p-3">
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
          />

          <Grid item xs={12} sx={{ mb: 3, mt: 2 }}>
            <InputField
              label="Password"
              fullWidth
              error={Boolean(touched.password && errors.password)}
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={values.password}
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                    title={showPassword ? 'Hide' : 'Show'}
                    size="small">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="********"
              inputProps={{}}
            />
          </Grid>

          {errors.submit && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Grid>
          )}

          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting || loading}
            className="w-100 mt-3">
            {isSubmitting || loading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              'Create Account'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export const EditCustomerForm = ({ customer, handleEditSubmit, loading }) => {
  const initialData = {
    ...initialCustomerState,
    ...customer,
    submit: null,
  };
  const safeInitialData = Object.fromEntries(
    Object.entries(initialData).map(([key, value]) => [
      key,
      value === null || value === undefined ? '' : value,
    ])
  );

  return (
    <Formik
      initialValues={safeInitialData}
      validationSchema={EditSchema}
      enableReinitialize={true}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleEditSubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Form noValidate onSubmit={handleSubmit} className="p-3">
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            isEdit={true}
          />

          <Alert variant="info" className="small">
            Note: Updating the customer details here will not change their
            password.
          </Alert>

          {errors.submit && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Grid>
          )}

          <Button
            variant="success"
            type="submit"
            disabled={isSubmitting || loading}
            className="w-100 mt-3">
            {isSubmitting || loading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};
